import {Button, Segment, SegmentProps} from 'platform/components';
import {Show, Stack, Text} from 'platform/foundation';

import {ReactNode} from 'react';

import {useGetTenantQuery} from '@dms/api/core';
import {useCreateEmptyPriceReportRequestMutation} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DEFAULT_COUNTRY, handleApiError, useHasMultipleBranches} from '@dms/shared';

import {SEGMENT_MIN_WIDTH} from '../consts/segmentMinWidth';
import {DashboardSource} from '../types/DashboardSource';
import {Clock} from './Clock';

interface ClockRowProps {
  dashboardSource: DashboardSource;
  setDashboardSource: (value: DashboardSource) => void;
  branchName?: string;
  actions?: ReactNode;
}

const getDashboardSourceOptions = (
  branchName?: string
): NonNullable<SegmentProps<DashboardSource>['options']> => [
  {
    value: DashboardSource.activeBranch,
    label: branchName ?? i18n.t('page.homepage.labels.myBranch'),
  },
  {
    value: DashboardSource.tenant,
    label: i18n.t('page.homepage.labels.all'),
  },
];

export function ClockRow(props: ClockRowProps) {
  const [createPriceReport, {isLoading: isCreatePriceReportLoading}] =
    useCreateEmptyPriceReportRequestMutation();
  const sourceOptions = getDashboardSourceOptions(props.branchName);
  const {data: tenant} = useGetTenantQuery();
  const tenantCountry = tenant?.country ?? DEFAULT_COUNTRY;
  const {hasMultipleBranches} = useHasMultipleBranches();

  const handleCreateNewPriceReport = () => {
    createPriceReport({
      createPriceReportRequestRequestBody: {country: tenantCountry},
    })
      .unwrap()
      .then((data) => {
        window.open(data.url, '_blank');
      })
      .catch(handleApiError);
  };

  return (
    <Stack
      direction={['row']}
      spacing={[3, 2]}
      align="center"
      justify={hasMultipleBranches ? 'space-between' : 'flex-end'}
    >
      <Show when={hasMultipleBranches}>
        <Segment
          value={props.dashboardSource}
          onChange={props.setDashboardSource}
          options={sourceOptions}
          minWidth={SEGMENT_MIN_WIDTH}
        />
      </Show>

      <Stack direction={['column', 'row']} spacing={2} align="center">
        <Text data-testid={testIds.dashboard.home('date')} size="small">
          <Clock />
        </Text>
        <Show whenFeatureEnabled={featureFlags.DASHBOARD_FAST_PRICING_BUTTON}>
          <Button
            variant="primary"
            onClick={handleCreateNewPriceReport}
            title={i18n.t('entity.priceReport.actions.createFastPriceReport')}
            isLoading={isCreatePriceReportLoading}
          />
        </Show>
        {props.actions}
      </Stack>
    </Stack>
  );
}
